import { useState } from 'react';

import { Heading, Modal, Paragraph } from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { useQueryClient } from '@zg-rentals/react-query5';
import { AccountViewListingGroupType, type RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyDetailsWebApiHandler_overview } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useArchiveMultiplexProperty, useUnarchiveMultiplexProperty } from '../../../hooks/queries/multiplex';
import { applyArchiveCacheUpdates } from '../../../hooks/queries/propertyTable/archiveCacheUpdates';
import { useArchiveProperty } from '../../../hooks/queries/useArchiveProperty';
import { useUnarchiveProperty } from '../../../hooks/queries/useUnarchiveProperty';
import propertyUtils from '../../../utils/propertyUtils';

import ModalFooter from '../ModalFooter';

export const ARCHIVE_TARGET_NOUN = {
  PROPERTY: 'property',
  UNIT: 'unit',
} as const;

export type ArchiveTargetNoun = (typeof ARCHIVE_TARGET_NOUN)[keyof typeof ARCHIVE_TARGET_NOUN];

type Props = {
  onClose: () => void;
  isArchived: boolean;
  listingAlias: string;
  groupId: string;
  address: RentalManagerAddressDto;
  targetNoun: ArchiveTargetNoun;
  listingType: AccountViewListingGroupType;
};

const FlexibleArchivePropertyModal = ({
  onClose,
  isArchived,
  listingAlias,
  groupId,
  address,
  targetNoun,
  listingType,
}: Props) => {
  const queryClient = useQueryClient();

  const { formattedCompleteAddress } = propertyUtils.formatAddress({
    ...address,
  });
  const [hasArchivingError, setHasArchivingError] = useState(false);

  const bodyText = isArchived
    ? `${formattedCompleteAddress} will be restored to your Properties page.`
    : `If you no longer own or manage ${formattedCompleteAddress}, archive it to remove it from your properties.`;
  const linkText = isArchived ? 'for rent & off market' : 'archived';
  const headerText = isArchived ? `Remove ${targetNoun} from archive?` : `When to archive a ${targetNoun}`;
  const modalHeader = `${isArchived ? 'Unarchive' : 'Archive'} ${targetNoun}`;

  /*
   * Default tag invalidation lives on the archive/unarchive hooks, and the per-row status/options
   * cells are handled by applyArchiveCacheUpdates. getPropertyTableUnits is invalidated more
   * precisely in onSuccess so only the affected group's units table refetches.
   */
  const { archiveProperty, isPending: isArchivingProperty } = useArchiveProperty();
  const { archiveMultiplexProperty, isPending: isArchivingMultiplex } = useArchiveMultiplexProperty();
  const { unarchiveProperty, isPending: isUnarchiving } = useUnarchiveProperty();
  const { unarchiveMultiplexProperty, isPending: isUnarchivingMultiplex } = useUnarchiveMultiplexProperty();

  const isSubmitting = isArchivingProperty || isArchivingMultiplex || isUnarchiving || isUnarchivingMultiplex;

  const handleUpdateArchiveStatus = () => {
    const mutationOptions = {
      onSuccess: () => {
        /*
         * These run alongside meta.invalidates from the shared MutationCache; overlapping
         * invalidations are safe since React Query dedupes concurrent refetches of the same query.
         */
        applyArchiveCacheUpdates(queryClient, {
          listingType,
          groupId,
          listingAlias,
          isUnarchiving: isArchived,
        });

        /*
         * The property overview query is read directly in many places without meta.tags, so it
         * isn't reachable via meta.invalidates yet; invalidate it explicitly for this alias.
         */
        queryClient.invalidateQueries({
          queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
        });

        // Only refetch the affected group's units table rather than every group's units.
        queryClient.invalidateQueries({
          predicate: ({ meta }) =>
            Boolean(meta?.tags?.includes('getPropertyTableUnits') && meta?.tags?.includes(groupId)),
        });

        onClose();
      },
      onError: () => {
        setHasArchivingError(true);
      },
    };

    switch (listingType) {
      case AccountViewListingGroupType.LISTING: {
        if (isArchived) {
          unarchiveProperty(listingAlias, mutationOptions);
        } else {
          archiveProperty(listingAlias, mutationOptions);
        }
        break;
      }
      case AccountViewListingGroupType.MULTIPLEX: {
        if (isArchived) {
          unarchiveMultiplexProperty(groupId, mutationOptions);
        } else {
          archiveMultiplexProperty(groupId, mutationOptions);
        }
        break;
      }
      default: {
        logError({
          error: new Error(`Unsupported listingType: ${listingType}`),
          errorType: 'FlexibleArchivePropertyModalUnsupportedListingType',
          context: { listingType, listingAlias, groupId, isArchived },
        });
        setHasArchivingError(true);
        break;
      }
    }
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', lg: 'md' }}
      header={<Heading level={2}>{modalHeader}</Heading>}
      body={
        <div data-test="modal-body">
          <Heading level={3} textStyle="heading-sm">
            {headerText}
          </Heading>
          <Paragraph data-test="body-text" css={{ marginBlock: '400' }}>
            {bodyText}
          </Paragraph>
          {!isArchived && (
            <Paragraph data-test="isNotArchivedMessage" css={{ marginBottom: '400' }}>
              You&#39;re still able to access the property by viewing
              {` ${linkText} `}
              properties from your Properties page.
            </Paragraph>
          )}
        </div>
      }
      footer={
        <ModalFooter
          submitText={isArchived ? 'Unarchive' : 'Archive'}
          onSubmit={handleUpdateArchiveStatus}
          onCancel={() => {
            setHasArchivingError(false);
            onClose();
          }}
          hasError={hasArchivingError}
          isSubmitting={isSubmitting}
        />
      }
    />
  );
};

export default FlexibleArchivePropertyModal;
